import { gql } from 'graphql-request';

export const PROCESS_EXECUTION_FIELDS = gql`
  fragment ProcessExecutionFields on ProcessExecutionDto {
    processExecutionId
    processId
    status
    startedAt
    completedAt
    createdAt
    assignerNote
    metadata
    processSnapshot
  }
`;

export const STEP_EXECUTION_FIELDS = gql`
  fragment StepExecutionFields on StepExecutionDto {
    stepExecutionId
    processExecutionId
    stepId
    sequenceOrder
    status
    startedAt
    completedAt
    notes
    errorDetails
    artifacts
    confirmationResponse
    stepSnapshot
    parentStepExecutionId
    iterationNumber
    substepOrder
    childStepExecutions {
      stepExecutionId
      processExecutionId
      stepId
      sequenceOrder
      status
      startedAt
      completedAt
      notes
      errorDetails
      confirmationResponse
      stepSnapshot
      parentStepExecutionId
      iterationNumber
      substepOrder
    }
  }
`;

export const getProcessExecutions = gql`
  query GetProcessExecutions($dateRange: DateRangeInput) {
    getProcessExecutions(dateRange: $dateRange) {
      ...ProcessExecutionFields
      startedByUser {
        userId
        firstName
        lastName
        email
      }
      assignedByUser {
        userId
        firstName
        lastName
        email
      }
      stepExecutions {
        ...StepExecutionFields
        step {
          stepId
          name
          stepType
          stepData
        }
      }
    }
  }
  ${PROCESS_EXECUTION_FIELDS}
  ${STEP_EXECUTION_FIELDS}
`;

export const getLatestExecutionHistory = gql`
  query GetLatestExecutionHistory($processId: String!) {
    getLatestExecutionHistory(processId: $processId) {
      ...ProcessExecutionFields
      isRedacted
      startedByUser {
        userId
        displayName
        email
      }
      stepExecutions {
        ...StepExecutionFields
      }
    }
  }
  ${PROCESS_EXECUTION_FIELDS}
  ${STEP_EXECUTION_FIELDS}
`;

export const getExecutionHistoryPaginated = gql`
  query GetExecutionHistoryPaginated($processId: String!, $pagination: PaginationInput) {
    getExecutionHistoryPaginated(processId: $processId, pagination: $pagination) {
      items {
        ...ProcessExecutionFields
        isRedacted
        startedByUser {
          userId
          displayName
          email
        }
        stepExecutions {
          ...StepExecutionFields
        }
      }
      pageInfo {
        totalCount
        count
        offset
        limit
        hasNextPage
        hasPreviousPage
      }
    }
  }
  ${PROCESS_EXECUTION_FIELDS}
  ${STEP_EXECUTION_FIELDS}
`;

export const getMyExecutions = gql`
  query GetMyExecutions($pagination: PaginationInput, $statusFilter: [ProcessExecutionStatus!], $dateRange: DateRangeInput) {
    getMyExecutions(pagination: $pagination, statusFilter: $statusFilter, dateRange: $dateRange) {
      items {
        ...ProcessExecutionFields
        process {
          processId
          name
        }
        startedByUser {
          userId
          firstName
          lastName
          email
        }
        assignedByUser {
          userId
          firstName
          lastName
          email
        }
        stepExecutions {
          ...StepExecutionFields
        }
      }
      pageInfo {
        totalCount
        count
        offset
        limit
        hasNextPage
        hasPreviousPage
      }
    }
  }
  ${PROCESS_EXECUTION_FIELDS}
  ${STEP_EXECUTION_FIELDS}
`;

export const getAssignedByMe = gql`
  query GetAssignedByMe($pagination: PaginationInput, $statusFilter: [ProcessExecutionStatus!], $dateRange: DateRangeInput) {
    getAssignedByMe(pagination: $pagination, statusFilter: $statusFilter, dateRange: $dateRange) {
      items {
        processExecutionId
        processId
        processSnapshot
        assignerNote
        process {
          name
          processId
        }
        startedByUser {
          userId
          firstName
          lastName
          email
          displayName
        }
        assignedByUser {
          userId
          firstName
          lastName
          displayName
        }
        stepExecutions {
          ...StepExecutionFields
        }
        status
        createdAt
        startedAt
        completedAt
      }
      pageInfo {
        totalCount
        count
        offset
        limit
        hasNextPage
        hasPreviousPage
      }
    }
  }
  ${STEP_EXECUTION_FIELDS}
`;

export const getMyPendingExecutionsCount = gql`
  query GetMyPendingExecutionsCount {
    getMyPendingExecutionsCount
  }
`;

export const getMyPendingExecutions = gql`
  query GetMyPendingExecutions($pagination: PaginationInput) {
    getMyPendingExecutions(pagination: $pagination) {
      items {
        processExecutionId
        processId
        status
        assignerNote
        process {
          processId
          name
        }
        startedByUser {
          userId
          displayName
        }
        assignedByUser {
          userId
          displayName
        }
        startedAt
        createdAt
      }
      pageInfo {
        totalCount
        count
        hasNextPage
        hasPreviousPage
      }
    }
  }
`;

export const getExecutionWithSteps = gql`
  query GetExecutionWithSteps($id: String!) {
    getExecutionWithSteps(id: $id) {
      ...ProcessExecutionFields
      startedByUser {
        userId
        displayName
        email
      }
      process {
        processId
        name
      }
      stepExecutions {
        ...StepExecutionFields
        step {
          stepId
          name
          stepType
          stepData
        }
        executedByUser {
          userId
          displayName
        }
      }
    }
  }
  ${PROCESS_EXECUTION_FIELDS}
  ${STEP_EXECUTION_FIELDS}
`;

export const getStepExecutionsByProcessExecution = gql`
  query GetStepExecutionsByProcessExecution($processExecutionId: String!) {
    getStepExecutionsByProcessExecution(processExecutionId: $processExecutionId) {
      ...StepExecutionFields
      step {
        stepId
        name
        stepData
      }
      executedByUser {
        displayName
      }
    }
  }
  ${STEP_EXECUTION_FIELDS}
`;

export const startProcessExecution = gql`
  mutation StartProcessExecution($input: ProcessExecutionCreateInput!) {
    startProcessExecution(processExecution: $input) {
      ...ProcessExecutionFields
      startedByUser {
        displayName
      }
      resumedFrom
      stepExecutions {
        ...StepExecutionFields
      }
    }
  }
  ${PROCESS_EXECUTION_FIELDS}
  ${STEP_EXECUTION_FIELDS}
`;

export const updateProcessExecution = gql`
  mutation UpdateProcessExecution($input: ProcessExecutionUpdateInput!) {
    updateProcessExecution(processExecution: $input) {
      ...ProcessExecutionFields
    }
  }
  ${PROCESS_EXECUTION_FIELDS}
`;

export const completeProcessExecution = gql`
  mutation CompleteProcessExecution($id: String!, $status: String!) {
    completeProcessExecution(id: $id, status: $status) {
      ...ProcessExecutionFields
    }
  }
  ${PROCESS_EXECUTION_FIELDS}
`;

export const createStepExecution = gql`
  mutation CreateStepExecution($input: StepExecutionCreateInput!) {
    createStepExecution(stepExecution: $input) {
      ...StepExecutionFields
    }
  }
  ${STEP_EXECUTION_FIELDS}
`;

export const updateStepExecution = gql`
  mutation UpdateStepExecution($input: StepExecutionUpdateInput!) {
    updateStepExecution(stepExecution: $input) {
      ...StepExecutionFields
    }
  }
  ${STEP_EXECUTION_FIELDS}
`;

export const updateStepExecutionStatus = gql`
  mutation UpdateStepExecutionStatus($id: String!, $status: String!) {
    updateStepExecutionStatus(id: $id, status: $status) {
      ...StepExecutionFields
    }
  }
  ${STEP_EXECUTION_FIELDS}
`;

export const deleteProcessExecution = gql`
  mutation DeleteProcessExecution($id: String!) {
    deleteProcessExecution(id: $id) {
      affected
    }
  }
`;

export const goBackToPreviousStep = gql`
  mutation GoBackToPreviousStep($processExecutionId: String!) {
    goBackToPreviousStep(processExecutionId: $processExecutionId) {
      stepExecutionId
      sequenceOrder
      status
      step {
        stepId
        name
      }
    }
  }
`;

export const assignProcessExecution = gql`
  mutation AssignProcessExecution($input: AssignProcessExecutionInput!) {
    assignProcessExecution(input: $input) {
      processExecutionId
      status
      startedBy
      startedAt
    }
  }
`;

// Substep execution queries
export const getSubstepExecutions = gql`
  query GetSubstepExecutions($processExecutionId: String!, $parentStepId: String!) {
    getSubstepExecutions(processExecutionId: $processExecutionId, parentStepId: $parentStepId) {
      ...StepExecutionFields
      step {
        stepId
        name
        stepType
        stepData
      }
    }
  }
  ${STEP_EXECUTION_FIELDS}
`;

export const getSubstepExecutionsByIteration = gql`
  query GetSubstepExecutionsByIteration($processExecutionId: String!, $parentStepId: String!, $iterationNumber: Int!) {
    getSubstepExecutionsByIteration(
      processExecutionId: $processExecutionId
      parentStepId: $parentStepId
      iterationNumber: $iterationNumber
    ) {
      ...StepExecutionFields
      step {
        stepId
        name
        stepType
        stepData
      }
    }
  }
  ${STEP_EXECUTION_FIELDS}
`;

export const getCurrentIterationNumber = gql`
  query GetCurrentIterationNumber($processExecutionId: String!, $parentStepId: String!) {
    getCurrentIterationNumber(processExecutionId: $processExecutionId, parentStepId: $parentStepId)
  }
`;

// Substep execution mutations
export const initializeSwitchSubsteps = gql`
  mutation InitializeSwitchSubsteps($processExecutionId: String!, $parentStepId: String!, $sequenceOrder: Int!) {
    initializeSwitchSubsteps(processExecutionId: $processExecutionId, parentStepId: $parentStepId, sequenceOrder: $sequenceOrder) {
      ...StepExecutionFields
      step {
        stepId
        name
        stepType
        stepData
      }
    }
  }
  ${STEP_EXECUTION_FIELDS}
`;

export const startNewIteration = gql`
  mutation StartNewIteration($processExecutionId: String!, $parentStepId: String!, $sequenceOrder: Int!) {
    startNewIteration(processExecutionId: $processExecutionId, parentStepId: $parentStepId, sequenceOrder: $sequenceOrder) {
      ...StepExecutionFields
      step {
        stepId
        name
        stepType
        stepData
      }
    }
  }
  ${STEP_EXECUTION_FIELDS}
`;

export const skipSteps = gql`
  mutation SkipSteps($stepExecutionIds: [String!]!, $notes: String) {
    skipSteps(stepExecutionIds: $stepExecutionIds, notes: $notes) {
      ...StepExecutionFields
      step {
        stepId
        name
        stepType
        stepData
      }
    }
  }
  ${STEP_EXECUTION_FIELDS}
`;

export const skipTriggerExecution = gql`
  mutation SkipTriggerExecution($triggerId: String!) {
    skipTriggerExecution(triggerId: $triggerId) {
      success
      message
    }
  }
`;

const ProcessExecutionGraphQL = {
  queries: {
    getProcessExecutions,
    getLatestExecutionHistory,
    getExecutionHistoryPaginated,
    getMyExecutions,
    getAssignedByMe,
    getMyPendingExecutionsCount,
    getMyPendingExecutions,
    getExecutionWithSteps,
    getStepExecutionsByProcessExecution,
    getSubstepExecutions,
    getSubstepExecutionsByIteration,
    getCurrentIterationNumber,
  },
  mutations: {
    startProcessExecution,
    updateProcessExecution,
    completeProcessExecution,
    createStepExecution,
    updateStepExecution,
    updateStepExecutionStatus,
    deleteProcessExecution,
    goBackToPreviousStep,
    assignProcessExecution,
    initializeSwitchSubsteps,
    startNewIteration,
    skipSteps,
    skipTriggerExecution,
  },
};

export default ProcessExecutionGraphQL;
